import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Users, FileText, AlertCircle, Download } from 'lucide-react';
import { accountsService } from '../lib/services.accounts';
import { useCurrency } from '../hooks/useCurrency';
import type { FinancialSummary, ReferralCommissionSummary } from '../lib/supabase';
import toast from 'react-hot-toast';

const AccountsManagement: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary[]>([]);
  const [referralSummary, setReferralSummary] = useState<ReferralCommissionSummary[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [financial, referral] = await Promise.all([
        accountsService.getFinancialSummary({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }),
        accountsService.getReferralCommissionSummary()
      ]);
      setFinancialSummary(financial);
      setReferralSummary(referral);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load accounts data');
    } finally {
      setLoading(false);
    }
  };

  const totals = financialSummary.reduce((acc, item) => ({
    revenue: acc.revenue + Number(item.total_revenue),
    collected: acc.collected + Number(item.total_collected),
    outstanding: acc.outstanding + Number(item.total_outstanding),
    discounts: acc.discounts + Number(item.total_discounts),
    refunds: acc.refunds + Number(item.total_refunds),
    invoices: acc.invoices + Number(item.total_invoices)
  }), { revenue: 0, collected: 0, outstanding: 0, discounts: 0, refunds: 0, invoices: 0 });

  const referralTotals = referralSummary.reduce((acc, item) => ({
    totalCommission: acc.totalCommission + Number(item.total_commission),
    paidCommission: acc.paidCommission + Number(item.paid_commission),
    pendingCommission: acc.pendingCommission + Number(item.pending_commission),
    totalReferrals: acc.totalReferrals + Number(item.total_referrals)
  }), { totalCommission: 0, paidCommission: 0, pendingCommission: 0, totalReferrals: 0 });

  const exportReport = () => {
    const csvContent = [
      ['Accounts Management Report'],
      [`Period: ${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Financial Summary'],
      ['Total Revenue', formatCurrency(totals.revenue)],
      ['Total Collected', formatCurrency(totals.collected)],
      ['Total Outstanding', formatCurrency(totals.outstanding)],
      ['Total Discounts', formatCurrency(totals.discounts)],
      ['Total Refunds', formatCurrency(totals.refunds)],
      ['Total Invoices', totals.invoices.toString()],
      [],
      ['Referral Commission Summary'],
      ['Total Commission', formatCurrency(referralTotals.totalCommission)],
      ['Paid Commission', formatCurrency(referralTotals.paidCommission)],
      ['Pending Commission', formatCurrency(referralTotals.pendingCommission)],
      ['Total Referrals', referralTotals.totalReferrals.toString()]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounts-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading accounts data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">Accounts Management</h1>
          <p className="text-neutral-600 mt-1">Comprehensive financial tracking and revenue analytics</p>
        </div>
        <button
          onClick={exportReport}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Export Report</span>
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 text-sm font-medium">Total Revenue</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(totals.revenue)}</p>
              <p className="text-primary-100 text-sm mt-1">{totals.invoices} invoices</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <DollarSign className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Collected */}
        <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-success-100 text-sm font-medium">Total Collected</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(totals.collected)}</p>
              <p className="text-success-100 text-sm mt-1">
                {totals.revenue > 0 ? ((totals.collected / totals.revenue) * 100).toFixed(1) : 0}% collection rate
              </p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <TrendingUp className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Outstanding */}
        <div className="bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warning-100 text-sm font-medium">Total Outstanding</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(totals.outstanding)}</p>
              <p className="text-warning-100 text-sm mt-1">Pending collection</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <AlertCircle className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Discounts */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Total Discounts</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">{formatCurrency(totals.discounts)}</p>
              <p className="text-neutral-500 text-sm mt-1">
                {totals.revenue > 0 ? ((totals.discounts / totals.revenue) * 100).toFixed(1) : 0}% of revenue
              </p>
            </div>
            <div className="bg-accent-100 rounded-full p-3">
              <TrendingDown className="w-6 h-6 text-accent-600" />
            </div>
          </div>
        </div>

        {/* Total Refunds */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Total Refunds</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">{formatCurrency(totals.refunds)}</p>
              <p className="text-neutral-500 text-sm mt-1">Refunded to patients</p>
            </div>
            <div className="bg-error-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-error-600" />
            </div>
          </div>
        </div>

        {/* Referral Commission */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Referral Commission</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">{formatCurrency(referralTotals.totalCommission)}</p>
              <p className="text-neutral-500 text-sm mt-1">
                {formatCurrency(referralTotals.pendingCommission)} pending
              </p>
            </div>
            <div className="bg-secondary-100 rounded-full p-3">
              <Users className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Referral Commission Details */}
      {referralSummary.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-xl font-semibold text-neutral-900">Referral Commission Summary</h2>
            <p className="text-sm text-neutral-600 mt-1">PC/Referral amounts by referrer</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Referrer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Total Referrals
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Service Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Total Commission
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {referralSummary.map((referral, index) => (
                  <tr key={index} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                      {referral.referrer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {referral.referrer_type ? referral.referrer_type.replace('_', ' ') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 text-right">
                      {referral.total_referrals}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 text-right">
                      {formatCurrency(Number(referral.total_service_amount))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 text-right">
                      {formatCurrency(Number(referral.total_commission))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-success-600 text-right">
                      {formatCurrency(Number(referral.paid_commission))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-warning-600 text-right">
                      {formatCurrency(Number(referral.pending_commission))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <a
          href="/accounts/payment-history"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="bg-primary-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Payment History</h3>
              <p className="text-sm text-neutral-600">View all payment transactions</p>
            </div>
          </div>
        </a>

        <a
          href="/accounts/receipts"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="bg-success-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-success-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Receipts</h3>
              <p className="text-sm text-neutral-600">Manage and generate receipts</p>
            </div>
          </div>
        </a>

        <a
          href="/billing"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="bg-accent-100 rounded-full p-3">
              <DollarSign className="w-6 h-6 text-accent-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Invoices</h3>
              <p className="text-sm text-neutral-600">View and manage invoices</p>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

export default AccountsManagement;
